import { useState, useRef, useEffect } from 'react';
import { X, Send, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';
import VoiceInput from './VoiceInput';
import ReactMarkdown from 'react-markdown';

const ROBOT_IMG = "https://customer-assets.emergentagent.com/job_e0f48102-226b-487d-acde-a79e93d00be9/artifacts/u3zz9toz_images-12.jpeg";

const SUGGESTIONS = [
  "Résumé de la semaine",
  "Qui est en difficulté ?",
  "Combien d'absences cette semaine ?",
  "Quels stagiaires ont les meilleures notes ?",
  "Quels EPMSP sont en attente ?",
  "Liste les formateurs",
];

const AIAssistant = ({ user }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [pendingAction, setPendingAction] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (text) => {
    if (!text.trim() || loading) return;
    const userMsg = { role: 'user', content: text.trim() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);
    setPendingAction(null);

    try {
      const res = await axios.post('/ai-assistant/chat', {
        message: text.trim(),
        session_id: sessionId,
        centre: user?.centre_formation || 'all'
      });
      if (res.data.session_id) setSessionId(res.data.session_id);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: res.data.response
      }]);
      if (res.data.action) setPendingAction(res.data.action);
    } catch (err) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "Désolé, une erreur s'est produite. Réessayez."
      }]);
    } finally {
      setLoading(false);
    }
  };

  const executeAction = async () => {
    if (!pendingAction) return;
    setActionLoading(true);
    try {
      const res = await axios.post('/ai-assistant/execute-action', {
        action_type: pendingAction.type,
        params: pendingAction.params
      });
      setMessages(prev => [...prev, {
        role: 'system',
        content: res.data.success ? `Action effectuée : ${res.data.message}` : `Erreur : ${res.data.message}`,
        success: res.data.success
      }]);
      setPendingAction(null);
    } catch {
      setMessages(prev => [...prev, {
        role: 'system',
        content: "Erreur lors de l'exécution de l'action.",
        success: false
      }]);
    } finally {
      setActionLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  if (user?.role !== 'centre' && user?.role !== 'responsable_pedagogique') return null;

  return (
    <>
      {/* Floating bubble */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-50 group"
          data-testid="ai-assistant-bubble"
        >
          <div className="relative">
            <div className="w-16 h-16 rounded-full bg-white shadow-xl border-2 border-red-400 flex items-center justify-center overflow-hidden transition-transform group-hover:scale-110">
              <img src={ROBOT_IMG} alt="Assistant FISL" className="w-14 h-14 object-contain" />
            </div>
            <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white animate-pulse" />
          </div>
          <p className="text-xs font-semibold text-slate-600 mt-1 text-center">Assistant</p>
        </button>
      )}

      {/* Chat panel */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 z-50 w-[400px] h-[560px] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden"
          data-testid="ai-assistant-panel"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-red-500 to-orange-500 px-4 py-3 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center overflow-hidden flex-shrink-0">
              <img src={ROBOT_IMG} alt="Assistant FISL" className="w-9 h-9 object-contain" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-white font-bold text-sm">Assistant FISL</h3>
              <p className="text-white/80 text-xs">Votre assistant IA</p>
            </div>
            <button onClick={() => setIsOpen(false)} className="text-white/80 hover:text-white transition-colors" data-testid="ai-assistant-close">
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50">
            {messages.length === 0 && (
              <div className="text-center py-4">
                <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-white shadow flex items-center justify-center overflow-hidden">
                  <img src={ROBOT_IMG} alt="" className="w-14 h-14 object-contain" />
                </div>
                <p className="text-sm font-semibold text-slate-700 mb-1">Bonjour ! Je suis l'Assistant FISL</p>
                <p className="text-xs text-slate-500 mb-4">Posez-moi une question sur vos stagiaires, notes, présences...</p>
                <div className="flex flex-wrap gap-1.5 justify-center">
                  {SUGGESTIONS.map((s, i) => (
                    <button
                      key={i}
                      onClick={() => sendMessage(s)}
                      className="px-2.5 py-1 bg-white border border-slate-200 rounded-full text-xs text-slate-600 hover:bg-red-50 hover:border-red-300 hover:text-red-600 transition-all"
                      data-testid={`suggestion-${i}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                {msg.role === 'assistant' && (
                  <div className="w-7 h-7 rounded-full bg-white shadow flex-shrink-0 flex items-center justify-center overflow-hidden mr-2 mt-1">
                    <img src={ROBOT_IMG} alt="" className="w-6 h-6 object-contain" />
                  </div>
                )}
                <div className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap ${
                  msg.role === 'user'
                    ? 'bg-red-500 text-white rounded-br-md'
                    : msg.role === 'system'
                    ? msg.success
                      ? 'bg-green-50 text-green-800 border border-green-200'
                      : 'bg-red-50 text-red-800 border border-red-200'
                    : 'bg-white text-slate-800 shadow-sm rounded-bl-md'
                }`}>
                  {msg.role === 'system' && (
                    <span className="inline-flex mr-1">
                      {msg.success ? <CheckCircle className="h-3.5 w-3.5 text-green-500" /> : <AlertCircle className="h-3.5 w-3.5 text-red-500" />}
                    </span>
                  )}
                  {msg.role === 'assistant' ? (
                    <div className="prose prose-sm prose-slate max-w-none [&>h2]:text-sm [&>h2]:font-bold [&>h2]:mt-2 [&>h2]:mb-1 [&>ul]:my-1 [&>ul]:pl-4 [&>p]:my-1 [&>ol]:my-1 [&>ol]:pl-4">
                      <ReactMarkdown>{msg.content}</ReactMarkdown>
                    </div>
                  ) : msg.content}
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-white shadow flex-shrink-0 flex items-center justify-center overflow-hidden">
                  <img src={ROBOT_IMG} alt="" className="w-6 h-6 object-contain" />
                </div>
                <div className="bg-white px-4 py-2 rounded-2xl shadow-sm">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-red-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <div className="w-2 h-2 bg-orange-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="w-2 h-2 bg-red-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}

            {/* Action confirmation */}
            {pendingAction && !actionLoading && (
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-3">
                <p className="text-xs font-medium text-orange-800 mb-2">
                  {pendingAction.label || 'Confirmer cette action ?'}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={executeAction}
                    className="flex-1 px-3 py-1.5 bg-orange-500 text-white text-xs font-medium rounded-lg hover:bg-orange-600 transition-colors"
                    data-testid="confirm-action-btn"
                  >
                    Confirmer
                  </button>
                  <button
                    onClick={() => setPendingAction(null)}
                    className="flex-1 px-3 py-1.5 bg-white text-slate-600 text-xs font-medium rounded-lg border hover:bg-slate-50 transition-colors"
                    data-testid="cancel-action-btn"
                  >
                    Annuler
                  </button>
                </div>
              </div>
            )}

            {actionLoading && (
              <div className="flex items-center gap-2 text-xs text-orange-600">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Exécution en cours...
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-3 border-t border-slate-200 bg-white">
            <div className="flex items-center gap-2">
              <div className="flex-1 relative">
                <input
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Posez votre question..."
                  className="w-full px-3 py-2.5 pr-10 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-400 focus:border-transparent"
                  disabled={loading}
                  data-testid="ai-assistant-input"
                />
                <VoiceInput
                  onTranscript={(text) => setInput(prev => prev ? `${prev} ${text}` : text)}
                  disabled={loading}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7"
                />
              </div>
              <button
                onClick={() => sendMessage(input)}
                disabled={!input.trim() || loading}
                className="w-10 h-10 flex items-center justify-center bg-red-500 hover:bg-red-600 disabled:bg-slate-300 text-white rounded-xl transition-colors flex-shrink-0"
                data-testid="ai-assistant-send"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AIAssistant;
